import type { ArticleStatus, ArticleSummary } from "@brandex/api-client";
import { formatRelative, type MessageKey } from "@brandex/i18n";
import { Alert, Badge, Button, EmptyState, Input, Spinner, cn } from "@brandex/ui";
import { FileText, Pin, PinOff, Plus } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState, useSyncExternalStore } from "react";

import { useBrandex, useT } from "../context";
import { errorMessage } from "../errors";
import { useCurrentOrg } from "../client-scope";
import { keys, mediaUrl, useArticles } from "../queries";
import { ArticleEditorPage } from "./article-editor";

type Tab = "all" | ArticleStatus;
const TABS: readonly Tab[] = ["all", "draft", "in_review", "published", "unpublished"];

const TONE: Record<ArticleStatus, "neutral" | "accent" | "success" | "warning"> = {
  draft: "neutral",
  in_review: "accent",
  published: "success",
  unpublished: "warning",
};

/** An article id is 24 hex characters; `new` starts a blank one. */
const TARGET = /^(new|[0-9a-f]{24})$/;

const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

const currentSearch = () => window.location.search;
/** On the server (static export) nothing is open. */
const serverSearch = () => "";

/**
 * Which article is open, kept in the address (`?edit=<id>`) so refresh and
 * the back button work. A query string, not a route segment: the desktop app
 * is a static export and cannot have dynamic routes.
 */
export function useEditTarget() {
  const search = useSyncExternalStore(subscribe, currentSearch, serverSearch);
  const raw = new URLSearchParams(search).get("edit");
  const target = raw !== null && TARGET.test(raw) ? raw : null;
  const go = useCallback((next: string | null, replace = false) => {
    const url = new URL(window.location.href);
    if (next === null) url.searchParams.delete("edit");
    else url.searchParams.set("edit", next);
    if (replace) window.history.replaceState(null, "", url);
    else window.history.pushState(null, "", url);
    listeners.forEach((l) => l());
  }, []);
  return { target, go };
}

/** Articles (F-32): write, review and publish articles on the doctor's website. */
export function ArticlesView({ autosaveMs }: { autosaveMs?: number }) {
  const t = useT();
  // The doctor's own practice, or the client staff maintain in the admin panel.
  const orgs = useCurrentOrg();
  const orgId = orgs.orgId;
  const { target, go } = useEditTarget();
  if (orgs.isPending) return <Spinner label={t("common.loading")} />;
  if (orgs.isError || orgId === undefined) return <Alert tone="error">{errorMessage(orgs.error, t)}</Alert>;
  if (target !== null) {
    return (
      <ArticleEditorPage
        key="editor"
        orgId={orgId}
        id={target === "new" ? null : target}
        // The new article's id goes in the address without leaving the editor.
        onCreated={(id) => go(id, true)}
        onClose={() => go(null)}
        {...(autosaveMs === undefined ? {} : { autosaveMs })}
      />
    );
  }
  return <ArticleList orgId={orgId} onOpen={(id) => go(id)} />;
}

/** Most articles pinned to the home page (the server's `Article::MAX_PINNED`). */
export const MAX_PINNED = 3;

function ArticleList({ orgId, onOpen }: { orgId: string; onOpen: (id: string) => void }) {
  const t = useT();
  const { locale, apiBaseUrl, api } = useBrandex();
  const qc = useQueryClient();
  const articles = useArticles(orgId);
  const pin = useMutation({
    mutationFn: async ({ id, pinned }: { id: string; pinned: boolean }) => {
      await api.http.PUT("/v1/orgs/{org_id}/articles/{article_id}/pin", {
        params: { path: { org_id: orgId, article_id: id } },
        body: { pinned },
      });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.articles(orgId) }),
  });
  const [tab, setTab] = useState<Tab>("all");
  const [query, setQuery] = useState("");
  const all = useMemo(() => articles.data ?? [], [articles.data]);
  const counts = useMemo(() => {
    const c: Record<Tab, number> = { all: all.length, draft: 0, in_review: 0, published: 0, unpublished: 0 };
    for (const a of all) c[a.status] += 1;
    return c;
  }, [all]);
  const pinnedCount = all.filter((a) => a.pinned_at !== null && a.pinned_at !== undefined).length;
  const publishedCount = counts.published;
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter(
      (a) =>
        (tab === "all" || a.status === tab) &&
        (q === "" || a.title.toLowerCase().includes(q) || a.excerpt.toLowerCase().includes(q) || a.tags.some((x) => x.includes(q))),
    );
  }, [all, tab, query]);

  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="font-display text-2xl font-bold">{t("articles.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("articles.subtitle")}</p>
        </div>
        <Button onClick={() => onOpen("new")}>
          <Plus className="h-4 w-4" aria-hidden />
          {t("articles.new")}
        </Button>
      </header>

      {articles.isPending ? <Spinner label={t("common.loading")} /> : null}
      {articles.isError ? <Alert tone="error">{errorMessage(articles.error, t)}</Alert> : null}

      {articles.isSuccess && all.length === 0 ? (
        <EmptyState icon={<FileText className="h-8 w-8 text-muted-foreground" aria-hidden />} title={t("articles.title")} description={t("articles.empty")} />
      ) : null}

      {articles.isSuccess && all.length > 0 ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div role="group" aria-label={t("articles.filter")} className="flex flex-wrap gap-1.5">
              {TABS.map((k) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={tab === k}
                  onClick={() => setTab(k)}
                  className="rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground"
                >
                  {t(`articles.tab.${k}` as MessageKey)} <span className="opacity-70">{counts[k]}</span>
                </button>
              ))}
            </div>
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("articles.search")}
              aria-label={t("articles.search")}
              className="ms-auto w-full sm:w-64"
            />
          </div>

          {publishedCount > 0 ? (
            <p className="flex items-start gap-2 rounded-[var(--radius-lg)] border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              <Pin className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
              <span>{t("articles.homeHint", { pinned: pinnedCount, max: MAX_PINNED })}</span>
            </p>
          ) : null}
          {pin.isError ? <Alert tone="error">{errorMessage(pin.error, t)}</Alert> : null}

          {shown.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("articles.noMatch")}</p>
          ) : (
            <ul className="grid gap-3">
              {shown.map((a) => (
                <ArticleRow
                  key={a.id}
                  article={a}
                  locale={locale}
                  apiBaseUrl={apiBaseUrl}
                  onOpen={() => onOpen(a.id)}
                  pinsFull={pinnedCount >= MAX_PINNED}
                  pinning={pin.isPending && pin.variables.id === a.id}
                  onPin={(pinned) => pin.mutate({ id: a.id, pinned })}
                />
              ))}
            </ul>
          )}
        </>
      ) : null}
    </div>
  );
}

function ArticleRow({
  article: a,
  locale,
  apiBaseUrl,
  onOpen,
  pinsFull,
  pinning,
  onPin,
}: {
  article: ArticleSummary;
  locale: "en" | "bn";
  apiBaseUrl: string;
  onOpen: () => void;
  pinsFull: boolean;
  pinning: boolean;
  onPin: (pinned: boolean) => void;
}) {
  const t = useT();
  const pinned = a.pinned_at !== null && a.pinned_at !== undefined;
  return (
    <li className={cn("glass flex items-center gap-2 rounded-[var(--radius-lg)] border p-1.5 pe-3 transition-colors focus-within:border-primary/60 hover:border-primary/60", pinned ? "border-primary/50" : "border-border")}>
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-4 rounded-[var(--radius-md)] p-1.5 text-start focus-visible:outline-2 focus-visible:outline-ring"
      >
        {a.cover ? (
          <img src={mediaUrl(apiBaseUrl, a.cover.url)} alt="" className="h-16 w-24 shrink-0 rounded-md object-cover" />
        ) : (
          <span className="grid h-16 w-24 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground" aria-hidden>
            <FileText className="h-6 w-6" />
          </span>
        )}
        <span className="grid min-w-0 flex-1 gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium" lang={a.locale}>{a.title}</span>
            <Badge tone={TONE[a.status]}>{t(`articles.status.${a.status}` as MessageKey)}</Badge>
            {pinned ? <Badge tone="accent">{t("articles.pinned")}</Badge> : null}
          </span>
          {a.excerpt !== "" ? <span className="line-clamp-1 text-sm text-muted-foreground" lang={a.locale}>{a.excerpt}</span> : null}
          <span className="text-xs text-muted-foreground">
            {t("articles.readingTime", { n: a.reading_minutes })} · {t("articles.updated", { when: formatRelative(locale, a.updated_at) })}
          </span>
        </span>
      </button>
      {a.status === "published" ? (
        <Button
          type="button"
          size="sm"
          variant={pinned ? "outline" : "ghost"}
          loading={pinning}
          disabled={!pinned && pinsFull}
          title={!pinned && pinsFull ? t("articles.pinsFull", { max: MAX_PINNED }) : undefined}
          aria-pressed={pinned}
          aria-label={t(pinned ? "articles.unpinLabel" : "articles.pinLabel", { title: a.title })}
          onClick={() => onPin(!pinned)}
        >
          {pinned ? <PinOff className="h-4 w-4" aria-hidden /> : <Pin className="h-4 w-4" aria-hidden />}
          <span className="hidden sm:inline">{t(pinned ? "articles.unpin" : "articles.pin")}</span>
        </Button>
      ) : null}
    </li>
  );
}
